import { useCallback } from 'react';
import { validateAgainstBannedPhrases } from '@/config/zenAfBannedPhrases';
import { generateMeditationAsync } from '@/utils/meditationApi';
import { useToast } from '@/hooks/use-toast';

interface ValidationResult {
  isAcceptable: boolean;
  originalityScore: number;
  foundBannedPhrases: string[];
  suggestions: string[];
  needsRegeneration: boolean;
}

export const useZenAfContentValidator = () => {
  const { toast } = useToast();

  const validateGeneratedContent = useCallback(async (content: string): Promise<ValidationResult> => {
    console.log('🔍 Validating ZEN AF content for banned phrases and originality...');
    
    const validation = validateAgainstBannedPhrases(content);
    
    // Consider content unacceptable if it has multiple banned phrases or very low originality
    const needsRegeneration = validation.foundPhrases.length > 2 || validation.originalityScore < 60;
    
    console.log('📊 Validation results:', {
      originalityScore: validation.originalityScore,
      bannedPhrasesCount: validation.foundPhrases.length,
      needsRegeneration,
      foundPhrases: validation.foundPhrases.slice(0, 5) // Log first 5
    });
    
    return {
      isAcceptable: !validation.hasBannedPhrases || validation.originalityScore >= 60,
      originalityScore: validation.originalityScore,
      foundBannedPhrases: validation.foundPhrases,
      suggestions: validation.suggestions,
      needsRegeneration
    };
  }, []);

  const regenerateWithAvoidList = useCallback(async (
    originalPrompt: string,
    bannedPhrasesFound: string[],
    attempt: number = 1
  ): Promise<{ content: string; audioUrl: string } | null> => {
    
    if (attempt > 2) {
      console.log('⚠️ Max regeneration attempts reached');
      return null;
    }

    console.log(`🔄 Regenerating content (attempt ${attempt}) with avoid list:`, bannedPhrasesFound.slice(0, 5));
    
    // Enhanced prompt with specific avoidance instructions
    const enhancedPrompt = `${originalPrompt}

CRITICAL ORIGINALITY REQUIREMENTS - AVOID ALL REPETITIVE PATTERNS:
- BANNED PHRASES DETECTED IN PREVIOUS GENERATION: ${bannedPhrasesFound.join(', ')}
- MANDATORY: Create completely fresh therapeutic language using biographical details
- AVOID: All generic template phrases, repetitive compound insults, overused metaphors
- REQUIREMENT: Generate original expressions from the user's actual life circumstances
- FOCUS: Use their specific biographical details for authentic personalized validation
- CREATIVITY: Combine their real-world context with fresh therapeutic language naturally
- AUTHENTICITY: Every creative element must be derived from their unique situation

REGENERATION MANDATE: This session must be 100% original and personalized - use natural language that serves their healing.`;

    try {
      // Call the generation function with enhanced anti-template prompt
      const { data, error } = await generateMeditationAsync({
        form_type: 'personalized',
        prompt: enhancedPrompt,
        usePersonalBiography: true
      }, {
        onProgress: (progress, message) => {
          console.log(`📊 Regeneration progress: ${Math.round(progress * 100)}% - ${message}`);
        }
      });

      if (error) throw error;
      
      if (data?.content) {
        // Validate the regenerated content
        const revalidation = await validateGeneratedContent(data.content);
        
        if (revalidation.needsRegeneration && attempt < 2) {
          console.log('🔄 Regenerated content still needs improvement, trying once more...');
          return regenerateWithAvoidList(originalPrompt, revalidation.foundBannedPhrases, attempt + 1);
        }
        
        console.log('✅ Regenerated content meets quality standards');
        return {
          content: data.content,
          audioUrl: data.audioUrl || ''
        };
      }
      
      return null;
    } catch (error) {
      console.error('❌ Error regenerating content:', error);
      return null;
    }
  }, [validateGeneratedContent]);

  const validateAndRegenerateIfNeeded = useCallback(async (
    content: string,
    audioUrl: string,
    originalPrompt: string
  ): Promise<{ content: string; audioUrl: string; wasRegenerated: boolean }> => {
    
    const validation = await validateGeneratedContent(content);
    
    if (!validation.needsRegeneration) {
      console.log('✅ Content passes validation checks');
      return { content, audioUrl, wasRegenerated: false };
    }
    
    console.log('⚠️ Content needs regeneration due to repetitive patterns');
    toast({
      title: "Improving Content Quality",
      description: "Regenerating session to avoid repetitive patterns and ensure originality...",
      duration: 3000,
    });
    
    const regenerated = await regenerateWithAvoidList(originalPrompt, validation.foundBannedPhrases);
    
    if (regenerated) {
      toast({
        title: "Content Enhanced",
        description: "Successfully generated more original and personalized content!",
        duration: 3000,
      });
      
      return {
        content: regenerated.content,
        audioUrl: regenerated.audioUrl,
        wasRegenerated: true
      };
    } else {
      console.log('⚠️ Regeneration failed, using original content');
      return { content, audioUrl, wasRegenerated: false };
    }
  }, [validateGeneratedContent, regenerateWithAvoidList, toast]);

  return {
    validateGeneratedContent,
    validateAndRegenerateIfNeeded,
    regenerateWithAvoidList
  };
};